/**
 * ProfilePage — personal admin / agent profile.
 * White info board over the management-photo background.
 * Gold + black palette. Plain "Back to dashboard" link sits in the top-right corner.
 * No "My Profile" title. Sign Out = black button with gold text (not a circle).
 * For NECS admin, "Property" → "Department".
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Shield, IdCard, Briefcase, LogOut,
  Map, Building2, Clock, Languages, Truck, Hash, Layers, Camera, Trash2,
  Pencil, Check, X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../utils/initials';
import { resolveRegionLabel } from '../utils/dfwRegions';
import { shiftLabel, STAFF_SHIFTS } from '../utils/staffShifts';
import ProfileFAQ from '../components/ProfileFAQ';
import BiometricSettings from '../components/BiometricSettings';
import NotificationPreferences from '../components/NotificationPreferences';
import SetPasswordCard from '../components/SetPasswordCard';
import TwoFactorSettings from '../components/TwoFactorSettings';
import RecentSignIns from '../components/RecentSignIns';
import DeleteAccountCard from '../components/DeleteAccountCard';
import DepartmentDirectoryCard from '../components/DepartmentDirectoryCard';
import ResidentRewardBadge from '../components/ResidentRewardBadge';
import { accountPosition, profileDisplayName } from '../lib/accountIdentity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Role → editable fields matrix on the Profile page (Feb 2026):
 *   Residents:      email + phone
 *   Staff:          email + phone + photo
 *   Property Mgr:   email + phone + photo
 *   Admin / CEO:    email + phone + photo   (they also edit anyone else via
 *                                              dedicated admin panels)
 *
 * Field labels that are NOT in the returned set render a small "🔒 Locked by
 * Management" hint next to them so residents/staff understand why they can't
 * edit their own name or address here.
 */
const editablePermissions = (role) => {
  if (role === 'admin' || role === 'company_manager') {
    return { email: true, phone: true, photo: true, note: 'Global editing (Admin/CEO)' };
  }
  if (role === 'property_manager') {
    return { email: true, phone: true, photo: true, note: 'You can also edit resident contact info from the Residents panel.' };
  }
  const staffRoles = ['supervisor', 'crew_leader', 'agent', 'site_agent', 'staff', 'manager', 'driver'];
  if (staffRoles.includes(role)) {
    return { email: true, phone: true, photo: true, note: 'Contact info + profile picture editable. Ask your supervisor to update anything else.' };
  }
  // Tenant / resident default — email + phone only, no photo.
  return { email: true, phone: true, photo: false, note: 'Contact info only. Ask your Property Manager to update anything else.' };
};

const PROFILE_BG_IMAGE =
  "url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/2pojcawf_2171.png')";

// NECS company defaults for the admin profile
const NECS_COMPANY_ADDRESS = '2450 Lakeside Pkwy, Ste 150, Flower Mound, TX 75022';
const NECS_ADMIN_PHONE = '+1 (888) 241-3077';
const NECS_ADMIN_POSITION = 'Chief Executive Officer';
const NECS_ADMIN_DEPARTMENT = 'Executive Office C-Suite';
// Feb 2026 — canonical admin office hours across the entire app. Update
// here and every admin profile page + directory-adjacent surface picks it
// up automatically.
const NECS_ADMIN_OFFICE_HOURS = 'Mon – Fri · 8:30 AM – 4:30 PM';

// Format a raw E.164 number (e.g. "+18882413077") into the canonical
// display form "+1 (888) 241-3077" used everywhere admins are shown.
// Non-US numbers are returned as-is.
const formatAdminPhone = (raw) => {
  if (!raw) return '';
  const digits = String(raw).replace(/[^\d]/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
};

// Department → PBX extension. Admin/CEO profiles show the extension inline
// next to the shared company phone number so callers can reach the right desk
// directly. Keep this map in sync with the phone system config.
const NECS_DEPARTMENT_EXTENSIONS = {
  'Executive Office C-Suite': '101',
  'Operations': '102',
  'Finance & Accounting': '103',
  'Human Resources': '104',
  'Information Technology': '105',
  'Legal & Compliance': '106',
  'Sales & Business Development': '107',
  'Marketing & Communications': '108',
  'Customer Service': '109',
  'Procurement & Supply': '110',
  'Safety & Risk Management': '111',
  'Training & Development': '112',
};

export default function ProfilePage() {
  const { user, loading, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) setProfile(await res.json());
      } catch (_) {
        /* keep existing user */
      }
    })();
  }, [user, loading, navigate]);

  const p = profile || user;
  const [tier, setTier] = useState(null);
  useEffect(() => {
    // Iter-78 pt 11 — fetch the caller's tier (Standard / Silver / Gold /
    // Platinum) for display as a badge on the profile card.
    if (!p?.user_id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/agent-status/me`, { credentials: 'include' });
        if (!cancelled && r.ok) setTier((await r.json()).tier || null);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [p?.user_id]);
  if (!p) return null;

  const permissions = editablePermissions(p.role);

  const patchProfileField = async (endpoint, body, successMsg) => {
    try {
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Update failed');
        return null;
      }
      toast.success(successMsg);
      return data;
    } catch (_) {
      toast.error('Network error');
      return null;
    }
  };

  const saveEmail = async (newEmail) => {
    const data = await patchProfileField('/api/auth/me/email', { email: newEmail }, 'Email updated');
    if (data?.email) setProfile((prev) => ({ ...(prev || user || {}), email: data.email }));
    return !!data?.email;
  };

  const savePhone = async (newPhone) => {
    const data = await patchProfileField(
      '/api/auth/me/phone',
      { phone: newPhone || null },
      newPhone ? 'Phone updated — confirmation SMS sent' : 'Phone number cleared',
    );
    if (data?.ok) {
      setProfile((prev) => ({ ...(prev || user || {}), phone: data.phone || null }));
      return true;
    }
    return false;
  };

  const isAdmin = p.role === 'admin' || p.role === 'company_manager';
  const isField = ['supervisor', 'manager', 'crew_leader', 'driver', 'agent', 'site_agent', 'staff'].includes(p.role);
  const isPropertyManager = p.role === 'property_manager';
  const isTenant = ['tenant', 'tenant_resident'].includes(p.role);
  const status = p.status || (isAdmin ? 'Active Admin' : isField ? 'Active Field Agent' : 'Active');

  // Resolved profile values with NECS defaults populated where empty.
  //
  // For admins we ALWAYS render the shared corporate line + department
  // extension. Even if the user has a personal cell on file, admins
  // publicly appear as `+1 (888) 241-3077 ext. NNN` so external callers
  // land in the PBX and get routed to the right department. The personal
  // `phone` field remains editable via PATCH /me/phone for internal
  // Twilio notifications only.
  const departmentForExtension = isAdmin ? (p.department || NECS_ADMIN_DEPARTMENT) : null;
  const phoneExtension = isAdmin
    ? (p.phone_extension || NECS_DEPARTMENT_EXTENSIONS[departmentForExtension] || null)
    : null;
  const rawPhoneForAdmin = isAdmin ? NECS_ADMIN_PHONE : (p.phone || '—');
  const displayPhone = isAdmin ? NECS_ADMIN_PHONE : formatAdminPhone(p.phone || '') || '—';
  const phoneValue = isAdmin
    ? (phoneExtension ? `${displayPhone} ext. ${phoneExtension}` : displayPhone)
    : rawPhoneForAdmin;
  // For NECS admins, the address is always the corporate HQ — no personal
  // address is ever shown on an admin profile per company policy. Every
  // other role composes their address from their own stored fields.
  const addressValue = isAdmin
    ? NECS_COMPANY_ADDRESS
    : ([p.address, p.unit_number ? `Unit ${p.unit_number}` : null, p.city, p.state].filter(Boolean).join(', ') || '—');
  const positionValue = accountPosition(p);

  // Composed full name (preferred over `name` when First/Middle/Last are set)
  const displayName = profileDisplayName(p);

  // Shift label — pretty version of the stored shift code (e.g. "Late Evening · 6:30 PM – 12:30 AM")
  const shiftDisplay = p.assigned_shift
    ? (STAFF_SHIFTS[p.assigned_shift]
        ? shiftLabel(p.assigned_shift)
        : (p.shift_schedule?.label || p.assigned_shift))
    : (p.shift_schedule?.label || (isAdmin ? NECS_ADMIN_OFFICE_HOURS : '—'));

  // Region + city resolution — accept either stored code (R02) or legacy free text
  const regionLabel = resolveRegionLabel(p.region) || '—';

  // Last detail card adapts to the user kind so admins see Department, field
  // agents see Field Role · Region, and tenants/PMs keep their Property.
  let lastFieldLabel = 'Property';
  let lastFieldValue = p.property_name || p.property_id || '—';
  if (isAdmin) {
    lastFieldLabel = 'Department';
    lastFieldValue = p.department || NECS_ADMIN_DEPARTMENT;
  } else if (isField) {
    lastFieldLabel = 'Field Role · Region';
    lastFieldValue = [p.role_type, regionLabel !== '—' ? regionLabel : null].filter(Boolean).join(' · ') || '—';
  }

  const goBack = () => {
    // Mirror the post-login routing in LoginPage / AuthCallback so the
    // "Back to dashboard" button never drops field-ops users onto the
    // legacy DriverDashboard layout. Supervisors, crew leaders and site
    // agents all share the modern StaffDashboard chrome; only `manager`
    // and `driver` still use the simpler DriverDashboard route.
    const dest = {
      admin: '/company-manager-dashboard',
      company_manager: '/company-manager-dashboard',
      // Iter-78 pt 9 · Regional Manager now routes to the unified Staff
      // dashboard alongside supervisors + crew leaders + agents (was
      // incorrectly falling through to /driver-dashboard which then
      // redirected the RM to the tenant view — hence the P0 bug).
      manager: '/staff-dashboard',
      supervisor: '/staff-dashboard',
      crew_leader: '/staff-dashboard',
      agent: '/staff-dashboard',
      site_agent: '/staff-dashboard',
      staff: '/staff-dashboard',
      driver: '/driver-dashboard',
      property_manager: '/property-manager-dashboard',
      tenant: '/tenant-dashboard',
    }[p.role] || '/';
    navigate(dest);
  };

  // Self-service profile picture upload — every role uses this same flow.
  // Reads the file, downsizes to max 512px on the longer edge via canvas,
  // JPEG-encodes at 0.82 quality (keeps it under ~250KB), then PUTs to
  // `/api/auth/me/profile-photo` so the change persists across sessions.
  const onPickPhoto = () => fileInputRef.current?.click();

  const onPhotoChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick a valid image file (JPG or PNG).');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Read failed'));
        reader.readAsDataURL(file);
      });
      // Downsize to keep the base64 payload small.
      const shrunk = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const maxEdge = 512;
          const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(c.toDataURL('image/jpeg', 0.82));
        };
        img.onerror = () => reject(new Error('Bad image'));
        img.src = dataUrl;
      });

      const res = await fetch(`${BACKEND_URL}/api/auth/me/profile-photo`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_photo: shrunk }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Upload failed');
      } else {
        toast.success('Profile picture updated');
        setProfile((prev) => ({ ...(prev || user || {}), profile_photo: shrunk }));
        try { window.dispatchEvent(new CustomEvent('profile-photo-updated', { detail: { photo: shrunk } })); } catch (_) { /* no-op */ }
      }
    } catch (_) {
      toast.error('Could not process image');
    }
    setUploading(false);
  };

  const removePhoto = async () => {
    if (!window.confirm('Remove your profile picture?')) return;
    setUploading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me/profile-photo`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_photo: null }),
      });
      if (res.ok) {
        toast.success('Profile picture removed');
        setProfile((prev) => ({ ...(prev || user || {}), profile_photo: null, picture: null }));
        try { window.dispatchEvent(new CustomEvent('profile-photo-updated', { detail: { photo: null } })); } catch (_) { /* no-op */ }
      }
    } catch (_) { /* noop */ }
    setUploading(false);
  };

  const avatarSrc = p.profile_photo || p.picture;

  return (
    <div
      className="min-h-screen relative py-10 px-4"
      style={{
        backgroundImage: PROFILE_BG_IMAGE,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#000000',
      }}
      data-testid="profile-page-wrapper"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.85) 100%)' }}
        data-testid="profile-bg-scrim"
      />

      <div className="relative max-w-3xl mx-auto min-h-[calc(100vh-5rem)] flex flex-col justify-center">
        {/* Plain (no circle) "Back to dashboard" link pinned to the top-right corner */}
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm font-bold hover:underline"
            style={{ color: '#C9A962', background: 'transparent', border: 'none' }}
            data-testid="profile-back"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </button>
        </div>

        {/* White admin information board — regular size, centered */}
        <div
          className="rounded-2xl p-8"
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #C9A962',
            boxShadow: '0 24px 48px rgba(0,0,0,0.45)',
          }}
          data-testid="profile-card"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8">
            <div className="relative group" data-testid="profile-avatar-block">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="h-20 w-20 rounded-full object-cover"
                  style={{ border: '2px solid #C9A962' }}
                  data-testid="profile-avatar-photo"
                />
              ) : (
                <span
                  className="h-20 w-20 rounded-full flex items-center justify-center text-2xl font-bold"
                  style={{ backgroundColor: '#C9A962', color: '#000000', border: '2px solid #000000' }}
                  data-testid="profile-avatar-fallback"
                >
                  {getInitials(p.name || p.email)}
                </span>
              )}
              {/* Change / Remove picture controls — Feb 2026: gated per role.
                  Residents/tenants see the avatar but cannot change it (the
                  Property Manager manages resident media). Staff, PMs, and
                  Admin/CEO all use the same `PUT /api/auth/me/profile-photo`
                  flow. */}
              {permissions.photo && (
                <button
                  type="button"
                  onClick={onPickPhoto}
                  disabled={uploading}
                  aria-label="Change profile picture"
                  className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full flex items-center justify-center shadow-md transition hover:scale-105 disabled:opacity-60"
                  style={{ backgroundColor: '#000000', color: '#C9A962', border: '2px solid #C9A962' }}
                  data-testid="profile-change-photo-btn"
                >
                  <Camera className="h-4 w-4" />
                </button>
              )}
              {permissions.photo && avatarSrc && (
                <button
                  type="button"
                  onClick={removePhoto}
                  disabled={uploading}
                  aria-label="Remove profile picture"
                  className="absolute -top-1 -right-1 h-6 w-6 rounded-full flex items-center justify-center shadow-md transition hover:scale-110 disabled:opacity-60"
                  style={{ backgroundColor: '#7f1d1d', color: '#fecaca', border: '2px solid #fca5a5' }}
                  data-testid="profile-remove-photo-btn"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPhotoChosen}
                data-testid="profile-photo-input"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="text-3xl font-bold [overflow-wrap:anywhere]"
                style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  color: '#000000',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
                data-testid="profile-name"
              >
                {displayName || 'Profile'}
              </h2>
              <p className="text-sm text-zinc-700 mt-2 break-words" data-testid="profile-position-heading">{positionValue}</p>
              <span
                className="inline-block text-[11px] font-bold uppercase tracking-wider mt-2 px-3 py-1 rounded-full"
                style={{ backgroundColor: '#000000', color: '#C9A962' }}
                data-testid="profile-status"
              >
                {status}
              </span>
              {/* Iter-78 pt 11 — Tier badge (Standard / Silver / Gold / Platinum). */}
              {tier && (
                <span
                  className="inline-block text-[11px] font-black uppercase tracking-wider mt-2 ml-2 px-3 py-1 rounded-full border-2"
                  style={{
                    backgroundColor:
                      tier === 'Platinum' ? '#e5e4e2' :
                      tier === 'Gold'     ? '#FFD60A' :
                      tier === 'Silver'   ? '#C0C0C0' :
                                            '#71717a',
                    color: tier === 'Gold' || tier === 'Silver' || tier === 'Platinum' ? '#000' : '#fff',
                    borderColor:
                      tier === 'Platinum' ? '#a3a3a3' :
                      tier === 'Gold'     ? '#EAB308' :
                      tier === 'Silver'   ? '#888' :
                                            '#52525b',
                  }}
                  data-testid="profile-tier-badge"
                >
                  ★ {tier}
                </span>
              )}

              {/* Iter-99 · Resident Reward Status Badge — Silver / Gold /
                  Platinum. Read-only. Sourced from Operations-level compute. */}
              {isTenant && (
                <span className="inline-block mt-2 ml-2 align-middle" data-testid="profile-resident-reward-slot">
                  <Link to="/resident-rewards" className="text-[#C9A962] underline text-sm" data-testid="profile-points-rewards-link">Points & bag rewards</Link>
                </span>
              )}
              <p className="text-[11px] text-zinc-500 mt-2">
                {uploading
                  ? 'Uploading…'
                  : permissions.photo
                    ? 'Click the camera icon to change your profile picture.'
                    : 'Profile picture is managed by your Property Manager.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 min-w-0">
            <div className="sm:col-span-2 min-w-0" data-testid="profile-name-contact-row">
              <p className="text-[11px] uppercase font-bold text-[#C9A962]">Name</p>
              <p className="text-sm font-bold text-black [overflow-wrap:anywhere]" data-testid="profile-contact-name">{displayName || '—'}</p>
            </div>
            <EditableDetail
              icon={Mail}
              label="Email"
              value={p.email}
              editable={permissions.email}
              lockedReason={permissions.note}
              placeholder="you@example.com"
              inputType="email"
              onSave={saveEmail}
              testid="profile-email"
            />
            <EditableDetail
              icon={Phone}
              label="Phone"
              value={p.phone || ''}
              displayValue={phoneValue}
              editable={permissions.phone}
              lockedReason={permissions.note}
              placeholder="+1 555 555 0100"
              inputType="tel"
              allowClear
              onSave={savePhone}
              testid="profile-phone"
              hint="Updating your phone triggers a Twilio confirmation SMS."
            />

            {/* Non-admin roles see a "Locked by Management" padlock on the
                read-only rows (Address, Unit, Position, Property, Badge,
                Building, Package) — the same set that never had inline edit
                affordances. Admin/CEO row labels stay unlocked because they
                can edit anyone from their dedicated dashboards. */}
            {(() => { /* short helper: is this profile field locked for the caller? */ })()}
            {/* Tenants see "Unit #" instead of street address — their
                building address belongs to the property, not the
                resident, and the Unit number is the actionable bit for
                pickup verification. */}
            {isTenant ? (
              <Detail
                icon={Hash}
                label="Unit #"
                value={p.unit_number || '—'}
                testid="profile-unit-top"
                locked={!isAdmin}
                lockedReason="Your unit assignment is set by your Property Manager."
              />
            ) : (
              <Detail
                icon={MapPin}
                label="Address"
                value={addressValue}
                testid="profile-address"
                locked={!isAdmin && !isPropertyManager}
                lockedReason="Your address is set by your Property Manager."
              />
            )}

            {/* Badge / ID row — hidden for tenants (residents don't carry
                a NECS badge). Everyone else (admin, field ops, PMs) still
                sees it. */}
            {!isTenant && (
              <Detail
                icon={IdCard}
                label="Badge / ID"
                value={p.badge_number || p.employee_number || '—'}
                testid="profile-badge"
                locked={!isAdmin}
                lockedReason="Badge numbers are issued by NECS Admin."
              />
            )}

            <Detail
              icon={Briefcase}
              label="Position"
              value={positionValue}
              testid="profile-position"
              locked={!isAdmin}
              lockedReason="Your position is set by NECS Management."
            />
            <Detail
              icon={Shield}
              label={lastFieldLabel}
              value={lastFieldValue}
              testid="profile-property"
              locked={!isAdmin}
              lockedReason={
                isTenant
                  ? "Your property assignment is set by your Property Manager."
                  : "Assignment is managed by NECS Admin."
              }
            />

            {/* Field-staff specific rows — Region on the left, Company HQ
                Address on the right (replaces the previously-empty City
                slot per CEO directive iter-81). The city coverage is still
                assigned by the Crew Leader and appears on the Team tab, so
                we don't lose that info — we just stop showing "—" on the
                profile card. */}
            {isField && (
              <>
                <Detail icon={Map} label="Region" value={regionLabel} testid="profile-region" locked lockedReason="Region is assigned by your Supervisor." />
                <Detail
                  icon={MapPin}
                  label="Company Address"
                  value={NECS_COMPANY_ADDRESS}
                  testid="profile-company-address"
                  locked
                  lockedReason="Corporate headquarters address."
                />
              </>
            )}

            {/* Admin-specific rows —
                NOTE (Feb 2026): Office Hours row intentionally OMITTED from
                the admin profile page per CEO directive. The `shift_schedule`
                field still lives on the DB record so HR reports and the AI
                Receptionist (Noble) can reference it for coverage questions
                — it's just not displayed on the admin's public-facing
                profile card. */}

            {/* Property Manager / Tenant extras — building, unit, package */}
            {(isPropertyManager || isTenant) && (
              <>
                {p.building_number && (
                  <Detail
                    icon={Layers}
                    label="Building"
                    value={p.building_number}
                    testid="profile-building"
                    locked={isTenant}
                    lockedReason="Your building assignment is set by your Property Manager."
                  />
                )}
                {p.unit_number && (
                  <Detail
                    icon={Hash}
                    label="Unit"
                    value={p.unit_number}
                    testid="profile-unit"
                    locked={isTenant}
                    lockedReason="Your unit assignment is set by your Property Manager."
                  />
                )}
                {p.package_type && (
                  <Detail
                    icon={Briefcase}
                    label="Package"
                    value={p.package_type}
                    testid="profile-package"
                    locked={isTenant}
                    lockedReason="Your service package is set by your Property Manager."
                  />
                )}
              </>
            )}

            {/* Preferred language — only shown when explicitly set, default 'en' is implied */}
            {p.preferred_language && p.preferred_language !== 'en' && (
              <Detail
                icon={Languages}
                label="Preferred Language"
                value={p.preferred_language.toUpperCase()}
                testid="profile-language"
              />
            )}
          </div>

          {/* iter-86 · Biometric login (WebAuthn) — enroll fingerprint / FaceID */}
          <div className="mt-6" data-testid="profile-biometric-section">
            <BiometricSettings />
          </div>

          <div
            className="mt-8 pt-6 flex flex-col sm:flex-row gap-3 items-center"
            style={{ borderTop: '1px solid rgba(0,0,0,0.12)' }}
          >
            <Link to="/privacy" className="text-sm font-bold hover:underline" style={{ color: '#C9A962' }} data-testid="profile-privacy-link">
              Privacy Policy
            </Link>
            <span className="hidden sm:inline" style={{ color: '#000000', opacity: 0.4 }}>·</span>
            <Link to="/terms" className="text-sm font-bold hover:underline" style={{ color: '#000000' }} data-testid="profile-terms-link">
              Terms of Service
            </Link>

            {/* Sign Out — black rectangular button, gold/yellow text (no circle) */}
            <button
              type="button"
              onClick={logout}
              className="sm:ml-auto inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-bold uppercase hover:opacity-90 transition"
              style={{
                backgroundColor: '#000000',
                color: '#C9A962',
                border: '1px solid #C9A962',
                letterSpacing: '0.08em',
              }}
              data-testid="profile-signout"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>

          {/* Set / Change password — unlocks dual-access (Google + email/password). */}
          <div className="mt-6">
            <SetPasswordCard />
          </div>

          {/* Iter-109 · Two-Factor Authentication (TOTP) + backup codes. */}
          <div className="mt-6" data-testid="profile-twofa-section">
            <TwoFactorSettings />
          </div>

          {/* Iter-110 · Item 6 — Recent Sign-Ins activity log. */}
          <div className="mt-6" data-testid="profile-signins-section">
            <RecentSignIns />
          </div>

          {/* NECS Department Directory — admin/CEO only. One-tap tel: links
              with auto-dialed extensions for every department. */}
          {isAdmin && (
            <div className="mt-6">
              <DepartmentDirectoryCard myDepartment={departmentForExtension} />
            </div>
          )}

          {/* Notification preferences — SMS vs Email routing for OTP + security alerts. */}
          <div className="mt-6">
            <NotificationPreferences />
          </div>

          {/* Danger zone — App Store / Google Play compliant self-service
              account deletion. Hidden for NECS admins / CEO because losing
              an executive account is a high-impact event that should only
              be triggered by the CEO directly through the DB manager, not
              through a self-service button on the Profile page. Residents
              and field staff see the button and the multi-step DELETE-
              typing confirmation as designed. */}
          {(
            <div className="mt-6">
              <DeleteAccountCard />
            </div>
          )}

          {/* Role-aware FAQs — always visible at the bottom of the profile.
              Restored old 3-tab structure (Property Management · Residents ·
              Commercial Business) as before the last deployment. */}
          <ProfileFAQ role={p.role} />
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value, testid, locked = false, lockedReason }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon className="h-4 w-4 mt-0.5" style={{ color: '#C9A962' }} />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#C9A962' }}>
            {label}
          </p>
          {locked && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-zinc-500"
              title={lockedReason || 'Locked by Management'}
              data-testid={`${testid}-locked`}
            >
              <Lock className="h-3 w-3" />
              Locked by Management
            </span>
          )}
        </div>
        <p className="text-sm font-bold [overflow-wrap:anywhere]" style={{ color: '#000000' }}>{value}</p>
      </div>
    </div>
  );
}

/**
 * EditableDetail — same visual language as `<Detail>` but supports inline
 * editing when `editable` is true. When `editable` is false, a small padlock
 * badge appears with the `lockedReason` in a tooltip so the user knows why
 * they can't change the field themselves.
 */
function EditableDetail({
  icon: Icon, label, value, displayValue, editable, lockedReason,
  placeholder, inputType = 'text', allowClear = false, onSave, testid, hint,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || ''); }, [value]);

  const submit = async () => {
    if (!onSave) return;
    setSaving(true);
    const ok = await onSave((draft || '').trim());
    setSaving(false);
    if (ok) setEditing(false);
  };

  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  return (
    <div className="flex items-start gap-3 min-w-0" data-testid={testid}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#C9A962' }} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#C9A962' }}>
            {label}
          </p>
          {!editable && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-zinc-500"
              title={lockedReason || 'Locked by Management'}
              data-testid={`${testid}-locked`}
            >
              <Lock className="h-3 w-3" />
              Locked by Management
            </span>
          )}
        </div>
        {editing ? (
          <div className="mt-1 flex items-center gap-2">
            <input
              type={inputType}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={placeholder}
              disabled={saving}
              className="flex-1 min-w-0 text-sm font-bold rounded border border-black/20 bg-white px-2 py-1"
              style={{ color: '#000' }}
              data-testid={`${testid}-input`}
              autoFocus
            />
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="p-1 rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-50"
              title="Save"
              data-testid={`${testid}-save`}
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              className="p-1 rounded hover:bg-red-50 text-red-700 disabled:opacity-50"
              title="Cancel"
              data-testid={`${testid}-cancel`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="mt-0.5 flex items-center gap-2 min-w-0">
            <p className="min-w-0 text-sm font-bold [overflow-wrap:anywhere]" style={{ color: '#000000' }}>
              {displayValue || value || '—'}
            </p>
            {editable && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="p-1 rounded hover:bg-black/5"
                title={`Edit ${label.toLowerCase()}`}
                data-testid={`${testid}-edit`}
              >
                <Pencil className="h-3.5 w-3.5" style={{ color: '#C9A962' }} />
              </button>
            )}
            {editable && allowClear && value && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Clear your ${label.toLowerCase()}?`)) return;
                  await onSave('');
                }}
                className="p-1 rounded hover:bg-red-50 text-red-700/70"
                title={`Clear ${label.toLowerCase()}`}
                data-testid={`${testid}-clear`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
        {editing && hint && (
          <p className="text-[10px] text-zinc-500 mt-1">{hint}</p>
        )}
      </div>
    </div>
  );
}
